// Port of ios/Sources/Models/PostEmbed.swift.
//
// The one preview card a post is worth (shared/API.md §12.4) — the server's `metadata.embeds`
// reduced to the first entry that can actually be drawn. Reduced at DECODE time and stored as
// one JSON column on the post row (`PostRecord.embedJson`), because the row rides every existing
// upsert path unchanged and the server recomputes embeds on every payload, so the column can
// never go stale.
//
// Only two kinds earn a card, matching what the desktop actually shows:
// - `opengraph`: an external page that offered a title/description. A bare `link` embed draws
//   nothing on the desktop either — the URL is already in the message text.
// - `permalink`: another Mattermost message, quoted — what a forwarded message renders as.

export interface PostEmbed {
  type: string;
  url: string;
  title: string;
  description: string;
  /** OpenGraph `site_name`, falling back to the URL's host — the card's eyebrow line. */
  siteName: string;
  targetPostId: string;
  targetChannelId: string;
  channelDisplayName: string;
  /**
   * The OpenGraph image (shared/API.md §12.4, 2026-10-17): already rewritten by the server's image
   * proxy to `<server>/api/v4/image?url=…` — fetched exactly as given, never wrapped again. '' = none.
   */
  imageUrl: string;
  /** Its size from `metadata.images[imageUrl]` (else the OG tags); 0 = unknown → a 1.91:1 box. */
  imageWidth: number;
  imageHeight: number;
}

const typeOpengraph = 'opengraph';
const typePermalink = 'permalink';
/**
 * A bare picture link (§40.1): drawn as a picture-only preview (the link's own image through the
 * server image proxy, site line = host), never as a text card; Core is not asked for it.
 */
const typeImage = 'image';

/** Something to draw as a card: text, or (OpenGraph, §40.1) an image alone. */
function isDrawable(embed: Pick<PostEmbed, 'title' | 'description'> & Partial<Pick<PostEmbed, 'type' | 'imageUrl'>>): boolean {
  if (embed.type === typeImage) return false;
  return embed.title !== '' || embed.description !== '' || (embed.type === typeOpengraph && !!embed.imageUrl);
}

/**
 * §40.1: does Mattermost's own embed preview this post's link? An `opengraph` embed with a title or an
 * image, an `image` embed, or a permalink quote — yes; nothing / `link` / an OpenGraph with only a
 * description — no (Core is asked for a web preview then).
 */
function hasUsablePreview(embed: PostEmbed | null): boolean {
  if (!embed) return false;
  if (embed.type === typeImage || embed.type === typePermalink) return true;
  return embed.type === typeOpengraph && (embed.title !== '' || embed.imageUrl !== '');
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A loosely-decoded optional string: anything that is not a string reads as absent. */
function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/**
 * The stored column back into a card, or null. Same contract as Swift's `Codable` decode: every
 * field must be present as a string (the encoder below always writes all eight), and a card
 * with no type or nothing to draw is no card.
 */
function fromJson(json: string): PostEmbed | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isObject(raw)) return null;
  const fields = [
    raw.type,
    raw.url,
    raw.title,
    raw.description,
    raw.site_name,
    raw.target_post_id,
    raw.target_channel_id,
    raw.channel_display_name,
  ];
  if (!fields.every((f) => typeof f === 'string')) return null;
  const embed: PostEmbed = {
    type: raw.type as string,
    url: raw.url as string,
    title: raw.title as string,
    description: raw.description as string,
    siteName: raw.site_name as string,
    targetPostId: raw.target_post_id as string,
    targetChannelId: raw.target_channel_id as string,
    channelDisplayName: raw.channel_display_name as string,
    // Optional: rows stored before 2026-10-17 carry no image (the next payload rewrites them).
    imageUrl: typeof raw.image_url === 'string' ? raw.image_url : '',
    imageWidth: num(raw.image_width),
    imageHeight: num(raw.image_height),
  };
  if (embed.type === typeImage) return embed.url ? embed : null;
  return embed.type === '' || !isDrawable(embed) ? null : embed;
}

/** The stored form: the same snake_case keys as the Swift `CodingKeys`. */
function encode(embed: PostEmbed): string {
  return JSON.stringify({
    type: embed.type,
    url: embed.url,
    title: embed.title,
    description: embed.description,
    site_name: embed.siteName,
    target_post_id: embed.targetPostId,
    target_channel_id: embed.targetChannelId,
    channel_display_name: embed.channelDisplayName,
    image_url: embed.imageUrl,
    image_width: embed.imageWidth,
    image_height: embed.imageHeight,
  });
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : 0;
}

/**
 * The first image of an OpenGraph `data.images` (`secure_url` before `url`), with its size from
 * `metadata.images[<that url>]` when the server measured it (OG width/height are often 0).
 */
function ogImage(data: Record<string, unknown>, metadata: Record<string, unknown>): { url: string; width: number; height: number } {
  const images = Array.isArray(data.images) ? data.images : [];
  const measured = isObject(metadata.images) ? metadata.images : {};
  for (const img of images) {
    if (!isObject(img)) continue;
    const candidates = [str(img.secure_url), str(img.url)].filter((u): u is string => !!u);
    if (!candidates.length) continue;
    const url = candidates[0];
    let width = 0;
    let height = 0;
    for (const c of candidates) {
      const m = measured[c];
      if (isObject(m) && num(m.width) && num(m.height)) {
        width = num(m.width);
        height = num(m.height);
        break;
      }
    }
    if (!width || !height) {
      width = num(img.width);
      height = num(img.height);
      if (!width || !height) width = height = 0;
    }
    return { url, width, height };
  }
  return { url: '', width: 0, height: 0 };
}

/** The OG image's drawn aspect (width / height): measured, else 1.91:1 (§12.4). */
function imageAspect(embed: Pick<PostEmbed, 'imageWidth' | 'imageHeight'>): number {
  return embed.imageWidth > 0 && embed.imageHeight > 0 ? embed.imageWidth / embed.imageHeight : 1.91;
}

/**
 * Where THIS client loads the image from. The proxy URL is absolute on the Mattermost host, which a
 * page served from another origin (the web client's chat domain) cannot authenticate against — so a
 * `/api/v4/image?url=…` URL is requested as the same path on the client's own server base (same
 * proxy, same session). Never re-wrapped. Anything else: https only, else nothing.
 */
function imageSrc(imageUrl: string, base: string): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('/api/v4/image?')) return base + imageUrl;
  let parsed: URL;
  try {
    parsed = new URL(imageUrl);
  } catch {
    return null;
  }
  if (parsed.pathname === '/api/v4/image') return base + parsed.pathname + parsed.search;
  return parsed.protocol === 'https:' ? imageUrl : null;
}

/** Swift's `.whitespaces` trim (spaces and tabs, not newlines). */
function isBlank(s: string): boolean {
  return s.replace(/[\p{Zs}\t]/gu, '') === '';
}

function hostOf(url: string): string {
  const at = url.indexOf('://');
  const afterScheme = at < 0 ? '' : url.slice(at + 3);
  return afterScheme.split('/')[0].split(':')[0];
}

/**
 * The first usable card in a post's `metadata` (the parsed JSON object the post payload
 * carries), or null when none of its embeds earns one. Walks `metadata.embeds` in the server's
 * order and keeps the FIRST usable entry — the desktop previews the first link too, and one card
 * per message is the ceiling anyway.
 *
 * Decoding is loose, like Swift's `EmbedDTO` (every field optional): a value of the wrong JSON
 * type reads as absent instead of failing the whole payload.
 */
function firstUsable(metadata: unknown): PostEmbed | null {
  if (!isObject(metadata) || !Array.isArray(metadata.embeds)) return null;
  for (const entry of metadata.embeds) {
    if (!isObject(entry)) continue;
    const data = isObject(entry.data) ? entry.data : null;
    switch (str(entry.type) ?? '') {
      case typeOpengraph: {
        if (!data) continue;
        const url = str(entry.url) ?? '';
        const title = str(data.title) ?? '';
        const description = str(data.description) ?? '';
        const image = ogImage(data, metadata);
        if (title === '' && description === '' && image.url === '') continue;
        const siteName = str(data.site_name);
        return {
          type: typeOpengraph,
          url,
          title,
          description,
          siteName: siteName ? siteName : hostOf(url),
          targetPostId: '',
          targetChannelId: '',
          channelDisplayName: '',
          imageUrl: image.url,
          imageWidth: image.width,
          imageHeight: image.height,
        };
      }
      case typePermalink: {
        if (!data) continue;
        const targetPostId = str(data.post_id);
        const targetChannelId = str(data.channel_id);
        if (!targetPostId || !targetChannelId) continue;
        const snippet = (isObject(data.post) ? str(data.post.message) : null) ?? '';
        if (isBlank(snippet)) continue;
        return {
          type: typePermalink,
          url: '',
          title: '',
          description: snippet,
          siteName: '',
          targetPostId,
          targetChannelId,
          channelDisplayName: str(data.channel_display_name) ?? '',
          imageUrl: '',
          imageWidth: 0,
          imageHeight: 0,
        };
      }
      default:
        // `link` (no OpenGraph data) and `image` draw nothing on the desktop either.
        continue;
    }
  }
  // No card: a picture link (§40.1) — drawn as a picture-only preview of the link itself, size from
  // `metadata.images[<url>]` when the server measured it. Counts as previewed (Core is not asked).
  const measured = isObject(metadata.images) ? metadata.images : {};
  for (const entry of metadata.embeds) {
    if (isObject(entry) && entry.type === typeImage) {
      const url = str(entry.url) ?? '';
      if (!url) continue;
      const m = measured[url];
      const width = isObject(m) ? num(m.width) : 0;
      const height = isObject(m) ? num(m.height) : 0;
      return { ...emptyEmbed, type: typeImage, url, siteName: hostOf(url), imageUrl: url, imageWidth: width && height ? width : 0, imageHeight: width && height ? height : 0 };
    }
  }
  return null;
}

const emptyEmbed: PostEmbed = {
  type: '',
  url: '',
  title: '',
  description: '',
  siteName: '',
  targetPostId: '',
  targetChannelId: '',
  channelDisplayName: '',
  imageUrl: '',
  imageWidth: 0,
  imageHeight: 0,
};

/** The serialized card for a post payload's `metadata` — what goes in `PostRecord.embedJson`. */
function firstUsableJson(metadata: unknown): string | null {
  const embed = firstUsable(metadata);
  return embed ? encode(embed) : null;
}

/**
 * A picture link that is one of the post's OWN files (a server file URL carrying its id): the file
 * row already draws it, so the picture preview is not drawn twice.
 */
function pictureIsAttached(url: string, fileIds: readonly string[]): boolean {
  if (!url) return false;
  let path: string;
  try {
    path = new URL(url, 'https://x.invalid').pathname;
  } catch {
    return false;
  }
  return fileIds.some((id) => !!id && path.split('/').includes(id));
}

export const PostEmbed = {
  pictureIsAttached,
  typeOpengraph,
  typePermalink,
  typeImage,
  isDrawable,
  hasUsablePreview,
  fromJson,
  encode,
  firstUsable,
  firstUsableJson,
  imageAspect,
  imageSrc,
} as const;
